import { c as _c } from "react/compiler-runtime";
import { useFeatureFlag, useIsMobile } from '@newfront-insurance/admin-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useMemo } from 'react';
import { NotificationsSettingsButton } from './notifications-settings-button';
import { ChatContainer } from '../components/chat-container';
import { ChatContent } from '../components/chat-content';
import { AccountSelector, ChatHeader, NewChatButton, ToggleSidebar } from '../components/chat-header';
import { ChatHeaderActions } from '../components/chat-header/chat-header-actions';
import { ChatRoot } from '../components/chat-root';
import { ChatSidebar, ChatSidebarFilterTabs, ChatSidebarHistory, ChatSidebarNewChat, ChatSidebarSearch, ChatSidebarSettings } from '../components/chat-sidebar';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { createDefaultDataPartRenderers, createDefaultToolRenderers } from '../lib/default-renderers';
import { NovaChatProvider } from '../providers';

/**
 * Container component that renders the chat UI.
 * Reads all props from context - no prop drilling needed.
 * All child components are self-contained and manage their own state.
 */
export function NovaChatContainer() {
  const $ = _c(32);
  const {
    isVisible,
    authProvider,
    userUuid,
    accountUuid,
    userScopes: scopes,
    initialMessages,
    isLoadingMessages: loading,
    placeholder,
    isStandaloneMode,
    isSidebarOpen,
    isFullscreen
  } = useChatOverlayContext();
  const isNovaNotificationsEnabled = useFeatureFlag("nova-notifications", false);
  const isNovaSourcesRenderingEnabled = useFeatureFlag("nova-sources-rendering", false);
  const {
    isMobile
  } = useIsMobile();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createDefaultToolRenderers();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  t0 = t1;
  const toolRenderers = t0;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = createDefaultDataPartRenderers();
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  t2 = t3;
  const dataPartRenderers = t2;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChatSidebarNewChat />;
    t5 = <ChatSidebarSearch />;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== isNovaNotificationsEnabled) {
    t6 = isNovaNotificationsEnabled && <ChatSidebarSettings><NotificationsSettingsButton /></ChatSidebarSettings>;
    $[4] = isNovaNotificationsEnabled;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChatSidebarFilterTabs />;
    t8 = <ChatSidebarHistory />;
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t6) {
    t9 = <ChatSidebar>{t4}{t5}{t6}{t7}{t8}</ChatSidebar>;
    $[8] = t6;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = (isStandaloneMode || isFullscreen) && isSidebarOpen && !isMobile;
  let t11;
  if ($[10] !== t10) {
    t11 = cn("flex size-full flex-col", {
      "ml-60": t10
    });
    $[10] = t10;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-center gap-4"><ToggleSidebar /><NewChatButton /></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChatHeader>{t12}<div className="flex flex-1 justify-center"><AccountSelector /></div><ChatHeaderActions /></ChatHeader>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== isNovaSourcesRenderingEnabled || $[15] !== loading || $[16] !== placeholder) {
    t14 = <ChatRoot>{t13}<ChatContent isLoading={loading} toolRenderers={toolRenderers} dataPartRenderers={dataPartRenderers} placeholder={placeholder} enableSourcesRendering={isNovaSourcesRenderingEnabled} /></ChatRoot>;
    $[14] = isNovaSourcesRenderingEnabled;
    $[15] = loading;
    $[16] = placeholder;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t11 || $[19] !== t14) {
    t15 = <div className={t11}>{t14}</div>;
    $[18] = t11;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== accountUuid || $[22] !== authProvider || $[23] !== initialMessages || $[24] !== scopes || $[25] !== t15 || $[26] !== t9 || $[27] !== userUuid) {
    t16 = <NovaChatProvider authProvider={authProvider} userUuid={userUuid} accountUuid={accountUuid} scopes={scopes} initialMessages={initialMessages}>{t9}{t15}</NovaChatProvider>;
    $[21] = accountUuid;
    $[22] = authProvider;
    $[23] = initialMessages;
    $[24] = scopes;
    $[25] = t15;
    $[26] = t9;
    $[27] = userUuid;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== isVisible || $[30] !== t16) {
    t17 = <ChatContainer isVisible={isVisible}>{t16}</ChatContainer>;
    $[29] = isVisible;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  return t17;
}